import Card from '../shared/cards/Card';
import LoaderContainer from '../shared/loaderContainer/LoaderContainer';
import UserRole from '../../assets/user-cog.svg';
import Asterix from '../../assets/asterix.svg';
import { useState, useEffect } from 'react';
import MenuButtonsPortal from '../Menu/MenuButtonsPortal';
import BiButton from '../primitives/buttons/BiButton.primitive';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { setToastData } from '../../app/toastReducer';
import ToastNotification from '../Reusable/ToastNotification';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  setFeatureData as setFeatureReducerData,
  setOrgList
} from '../../app/rolesAndFeatureReducer';
import moment from 'moment';

import { HTTP_STATUS } from '../../http/constants/http.status';
import {
  addOperationOrganizations,
  addOperationsToFeature,
  createFeature,
  deleteFeature,
  deleteFeatureOperations,
  getFeature,
  getPermissions,
  removeOperationOrganizations,
  updateFeature,
  updateFeatureOperations,
  updateOperationAPIPermissions
} from '../../http/access-management-service';
import { checkRoleFieldLength } from '../../util/RoleUtil';
import { AppConstants } from '../../constants/AppConstants';
import { getFailureMessage } from '../../util/ErrorUtil';
import { useTranslation } from 'react-i18next';
import {
  cloneDeep,
  differenceBy,
  uniq,
  compact,
  isEmpty,
  orderBy,
  intersectionBy,
  isFunction,
  isEqual,
  difference
} from 'lodash';
import { getLoggedInOrg, getUserName, getUserProfiles } from '../../util/admin-utils';
import InputField from '../shared/Fields/InputField';
import PickList, { PICKLIST_TYPES } from '../shared/Fields/PickList';
import TextAreaField from '../shared/Fields/TextAreaField';
import { RouteConstants } from '../../constants/RouteConstants';
import TabDrawer from '../Reusable/TabDrawer';
import AuditInfo from '../Common/AuditInfo';
import OperationsTag from './OperationsTag';
import Tags from './Tags';
import Modal from '../shared/ModalPortal/Modal';
import { ModalButton } from '../../styles/globalVariables';
import { checkFeaturePermission } from '../../util/permissionUtils';
import { getChildOrganizations, getOrganizations } from '../../http/organization-services';
import { getUserLoggedInOrg } from '../../util/OrganizationUtil';
import Stepper from '../Reusable/Stepper';
import { Step } from '../Onboarding/OnboardingStyles';
import { ActionButtons } from '../Reusable/ActionButton';
import { setBreadcrump } from '../../app/organizationReducer';

/**
 * @parentId feature
 * @manager Create, Read, Update, Delete - Feature
 * @overview Create, Read, Update, Delete - Feature
 * <section>
 *   <p>
 *     The Features page provides functionalities for managing features, including viewing, editing, creating, and deleting.
 *   </p>
 *   <br>
 *   <p>
 *     <h4>View Feature</h4>
 *     Navigate to the feature details page by clicking a feature row from the feature listing page. This opens in view-only mode.
 *     Return to the feature listing by clicking <b>Back to Features List</b>.
 *     Displays audit details: <b>Created By</b>, <b>Created On</b>, <b>Last Modified By</b>, <b>Last Modified On</b>.
 *   </p>
 *   <br>
 *   <p>
 *     <h4>Edit Feature</h4>
 *     Click <b>Edit</b> to toggle to editing mode. Use <b>Save</b> to apply changes or <b>Cancel</b> to revert.
 *   </p>
 *   <br>
 *   <p>
 *     <h4>Create Feature</h4>
 *     Accessed via the <b>Create New Feature</b> button.
 *     Requires <b>Feature Name</b>, <b>Description</b>, <b>Status</b>, at least 1 <b>Tag</b> and at least 1 <b>Operation</b> fields.
 *   </p>
 * </section>
 * <section>
 *   <h4>Delete Operation</h4>
 *   <p>
 *    Delete is only allowed if the feature is not assigned to any Role. User can delete an existing non assigned feature by clicking on <b>Delete Feature</b> button.
 *   </p>
 *   <br>
 * </section>
 *   <h5>Table Display:</h5>
 *   <ul>
 *     <li><b>Status:</b> Displays the feature status.</li>
 *     <li><b>Name:</b> Displays the feature name.</li>
 *     <li><b>Description:</b> Displays the feature description.</li>
 *     <li><b>Actions:</b> Lists operations associated with the feature.</li>
 *     <li><b>Tags:</b> Lists tags associated with the feature.</li>
 *     <li><b>Created On:</b> Shows the creation date of the feature.</li>
 *     <li><b>Last Modified On:</b> Shows the last modification date of the feature.</li>
 *   </ul>
 *   <h5>Validations:</h5>
 *   <ul>
 *     <li><b>Filters:</b> Ensure valid input types for text fields. Implement required checks for mandatory fields.</li>
 *     <li><b>Search and Pagination:</b> Validate that page numbers and sizes are within acceptable ranges.</li>
 *   </ul>
 *   <br>
 * </section>
 * <section>
 *   <h4>OperationsTag Component</h4>
 *   <p>Available Fields and Tabs:</p>
 *   <h5>Operation Details:</h5>
 *   <ul>
 *     <li><b>Action Name:</b> Text input for the operation's name.</li>
 *     <li><b>Description:</b> Text area for a detailed description of the operation.</li>
 *     <li><b>Status:</b> Picklist to select the operation status.</li>
 *     <li><b>API Permissions:</b> Dual list pick for selecting associated API permissions.</li>
 *   </ul>
 *   <h5>Validations:</h5>
 *   <ul>
 *     <li><b>Action Name:</b> Required field; ensure the input is not empty and within character limits.</li>
 *     <li><b>Description:</b> Required field; must be filled and within character limits.</li>
 *     <li><b>Status:</b> Must select at least one status from the picklist.</li>
 *     <li><b>API Permissions:</b> At least one permission must be selected.</li>
 *   </ul>
 *   <br>
 * </section>
 * <section>
 * </section>
 * <section>
 *   <h4>View/Create/Edit Feature Fields</h4>
 *   <p>Displays fields with validation constraints:</p>
 *   <table>
 *     <tr>
 *       <th>Field Name</th>
 *       <th>Required</th>
 *       <th>Data Type</th>
 *       <th>Field Type</th>
 *       <th>Max Length</th>
 *     </tr>
 *     <tr>
 *       <td>Feature Name</td>
 *       <td>Yes</td>
 *       <td>String</td>
 *       <td>Textfield</td>
 *       <td>NA</td>
 *     </tr>
 *      <tr>
 *       <td>Status</td>
 *       <td>Yes</td>
 *       <td>Enum</td>
 *       <td>Dropdown</td>
 *       <td>Valid enumerated values are ACTIVE andINACTIVE</td>
 *     </tr>
 *     <tr>
 *       <td>Description</td>
 *       <td>No</td>
 *       <td>String</td>
 *       <td>TextArea</td>
 *       <td>NA</td>
 *     </tr>
 *     <tr>
 *       <td>Tags</td>
 *       <td>No</td>
 *       <td>Tag_Write[]</td>
 *       <td>NA</td>
 *       <td>N/A</td>
 *     </tr>
 *      <tr>
 *       <td>operations</td>
 *       <td>Yes</td>
 *       <td>FeatureOperationDTO_Write[]</td>
 *       <td>NA</td>
 *       <td>N/A</td>
 *     </tr>
 *   </table>
 *   <br>
 * </section>
 *  <section>
 *   <h4>Tag_Write Definition</h4>
 *   <p>The <code>Tag_Write</code> type is used to represent tags that can be assigned to features. It includes the following properties:</p>
 *   <table>
 *     <tr>
 *       <th>Property Name</th>
 *       <th>Required</th>
 *       <th>Data Type</th>
 *       <th>Description</th>
 *     </tr>
 *     <tr>
 *       <td>key</td>
 *       <td>Yes</td>
 *       <td>String</td>
 *       <td>Tag key. The key of the tag, this has a limit of 1024 characters. This is mandatory.</td>
 *     </tr>
 *     <tr>
 *       <td>Expires On</td>
 *       <td>No</td>
 *       <td>String (date-time)</td>
 *       <td>Tag expired on. The date when the tag will expire.</td>
 *     </tr>
 *   </table>
 *   <br>
 * </section>
 *   <section>
 *   <h4>FeatureOperationDTO_Write[] Definition</h4>
 *   <p>The <code>FeatureOperationDTO_Write[]</code> type is used to represent operations that can be assigned to features. It is available when user clicks on <b>Actions</b> Tab in the Feature Page. User can create an operation by clicking on <b>Add New Operation</b> button. It includes the following properties:</p>
 *   <table>
 *     <tr>
 *       <th>Property Name</th>
 *       <th>Required</th>
 *       <th>Data Type</th>
 *       <th>Description</th>
 *     </tr>
 *     <tr>
 *       <td>Action Name</td>
 *       <td>Yes</td>
 *       <td>String</td>
 *       <td>The action which identifies the Operation. This should be case-insensitive.</td>
 *     </tr>
 *       <tr>
 *       <td>Status</td>
 *       <td>Yes</td>
 *       <td>Enum</td>
 *       <td>Reflects the step in the workflow. Valid enumerated values are ACTIVE and INACTIVE</td>
 *     </tr>
 *       <tr>
 *       <td>Description</td>
 *       <td>Yes</td>
 *       <td>String</td>
 *       <td>Feature description. Example: Feature for the management over Organizations in the platform.</td>
 *     </tr>
 *     <tr>
 *       <td>apiPermissions</td>
 *       <td>No</td>
 *       <td>String[]</td>
 *       <td>List of APIs the feature operation has access to. Example: [accesscontrol-service.role.get].</td>
 *     </tr>
 *   </table>
 *   <br>
 * </section>
 * <section>
 *   <h4>Failure Status Codes</h4>
 *   <p>Describes failure status codes for feature operations:</p>
 *   <table>
 *     <tr>
 *       <th>HTTP Status Code</th>
 *       <th>Service Error Code</th>
 *       <th>Error Message</th>
 *     </tr>
 *     <tr>
 *       <td>400</td>
 *       <td>ACS_PARAM_DUPLICATE</td>
 *       <td>There is already an existing entity with the given field value</td>
 *     </tr>
 *      <tr>
 *       <td>400</td>
 *       <td>ACS_NOT_ALLOWED</td>
 *       <td>The operation is not allowed</td>
 *     </tr>
 *     <tr>
 *       <td>401</td>
 *       <td>ACS_AUTH_REQUIRED</td>
 *       <td>The request has not been applied because it lacks valid authentication credentials for the target resource</td>
 *     </tr>
 *       <tr>
 *       <td>403</td>
 *       <td>ACS_PERMISSION_DENIED</td>
 *       <td>Request failed due to authorization permissions</td>
 *     </tr>
 *     <tr>
 *       <td>500</td>
 *       <td>ACS_INTERNAL_ERROR</td>
 *       <td>The server will not process the request due to a internal error</td>
 *     </tr>
 *     <tr>
 *       <td>503</td>
 *       <td></td>
 *       <td>Service Unavailable</td>
 *     </tr>
 *   </table>
 *   <br>
 * </section>
 * <section>
 *   <h5>Platform Services</h5>
 *   <p>Required platform services:</p>
 *   <table>
 *     <tr>
 *       <th>Service Name</th>
 *       <th>Version</th>
 *     </tr>
 *     <tr>
 *       <td>Access Control Service</td>
 *       <td>1.0.0</td>
 *     </tr>
 *     <tr>
 *       <td>User Management Service</td>
 *       <td>1.6.0</td>
 *     </tr>
 *   </table>
 *   <br>
 * </section>
 * <section>
 *   <p>Sequence Diagram for Feature management interaction flow:</p>
 * </section>
 */
interface AUDIT_SCHEMA {
  createdBy: string;
  updatedBy: string;
  createdOn: string;
  updatedOn: string;
}

export interface Tag {
  key: string;
  expiredOn?: null | string;
}

interface Manifest {
  page: number;
  searchText: string;
}

const FEATURES_FORM_SCHEMA = {
  name: '',
  description: '',
  status: ''
};

const FEATURE_AUDIT_SCHEMA = {
  createdBy: '',
  updatedBy: '',
  createdOn: '',
  updatedOn: ''
};

interface Props {
  setNewDataLoaded?: Function;
  hideModal?: Function;
}

const FeatureDetails = ({ setNewDataLoaded, hideModal = () => {} }: Props) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const editMode =
    params?.id &&
    !location.pathname.includes(
      RouteConstants.ROUTE_ROLE_MANAGEMENT_EDIT.replace(':id', params.id)
    );

  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<any>(FEATURES_FORM_SCHEMA);
  const [formDataClone, setFormDataClone] = useState<any>(FEATURES_FORM_SCHEMA);
  const [auditData, setAuditData] = useState<AUDIT_SCHEMA>(FEATURE_AUDIT_SCHEMA);
  const [formError, setFormError] = useState<any>(FEATURES_FORM_SCHEMA);
  const [operations, setOperations] = useState<Array<any>>([]);
  const [operationsClone, setOperationsClone] = useState<Array<any>>([]);
  const [tagInput, setTagInput] = useState<Tag>({ key: '', expiredOn: null });
  const [tags, setTags] = useState<Array<Tag>>([]);
  const [tagsClone, setTagsClone] = useState<Array<Tag>>([]);
  const [rolePermissions, setRolePermissions] = useState<any>([]);
  const [rolePermissionsClone, setRolePermissionsClone] = useState<any>([]);
  const [permissionSetInitialised, setPermissionSetInitialised] = useState<boolean>(false);
  const [operationsEditInitialised, setOperationsEditInitialised] = useState<boolean>(false);
  const [flowType, setFlowType] = useState<any>({
    isCreate: location.pathname.split('/').at(-1) === 'new',
    isEdit: editMode || false,
    isReadOnly: editMode || false
  });
  const [checkDeleteConfirm, setDeleteConfirmation] = useState<any>(false);
  const [activeTab, setActiveTab] = useState(`${t('T_FEATURE_DETAILS')}`);
  const [featureData, setFeatureData] = useState<any>(null);

  const toastData = useAppSelector((state) => state.toast);
  const storedURLPaths = useAppSelector((state) => state.organization.urlPaths);
  const featureReducerData = useAppSelector((state) => state.rolesAndFeature?.featureData);
  const orgList = useAppSelector((state) => state.rolesAndFeature?.orgList);
  const userProfile = useAppSelector((state) => state.userManagement.userProfileInfo);
  const loggedInOrg = getLoggedInOrg(userProfile);

  const isModalMode = isFunction(setNewDataLoaded);

  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    { step: 1, title: t('T_FEATURE_DETAILS') },
    { step: 2, title: t('T_FEATURE_ACTION_TAGS') }
  ];

  const handleNext = () => {
    if (activeStep == 1) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      savePayload();
    }
  };

  const handleBack = () => {
    if (activeStep !== 1) {
      setActiveStep((prevStep) => prevStep - 1);
    } else {
      hideModal();
    }
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 1:
        return (
          formData.name &&
          formData.status &&
          formData.description &&
          !formError.name &&
          !formError.description
        );
      case 2:
        return operations.length > 0;
      default:
        return true;
    }
  };

  useEffect(() => {
    async function init() {
      if (!editMode) {
        setBreadCrumb();
        setIsLoading(false);
      } else {
        await getAPIPermissions();
      }
    }
    init();
  }, []);

  useEffect(() => {
    if (editMode && isEmpty(featureData)) {
      if (!isEmpty(featureReducerData)) {
        saveEditResponse(featureReducerData);
        setFeatureData(featureReducerData);
      } else {
        saveEditResponse(null);
      }
    }
  }, [featureReducerData, featureData]);

  const getIsEditOrReadOnly = () => {
    const { isEdit, isReadOnly } = flowType;
    if (isEdit && isReadOnly) {
      return true;
    } else if (isEdit && !isReadOnly) {
      return false;
    }
    return false;
  };

  const setisReadOnly = (isReadOnly: boolean, toBeReverted: boolean) => {
    if (toBeReverted) {
      discardChanges();
    }
    setFlowType({ ...flowType, isEdit: editMode && isReadOnly, isReadOnly: isReadOnly });
  };

  const getAPIPermissions = async (rolePermissions: Array<any> = []) => {
    if (!permissionSetInitialised) {
      const response = await getPermissions(AppConstants.MAXIMUM_PAGE_SIZE);
      const { status, data } = response;
      if (HTTP_STATUS.isSuccess(status)) {
        const { data: permissionList = [] } = data;

        const orderedData = orderBy(
          differenceBy(permissionList, rolePermissions, 'id'),
          [AppConstants.ACCESS_CONTROL_API_PERMISSION_KEY],
          [AppConstants.DEFAULT_SORT_ASC]
        );
        setRolePermissions(orderedData);
        setRolePermissionsClone(orderedData);
        setPermissionSetInitialised(true);
      }
    }
  };

  const getObjects = (strings: Array<string>, objects: any) => {
    return strings
      .map((str) => objects.find((obj: any) => obj.apiPermission === str))
      .filter((obj: any) => obj !== undefined);
  };

  useEffect(() => {
    async function updateOperations() {
      async function init() {
        const [loggedInOrg, orgFeatures] = await Promise.all([
          getUserLoggedInOrg(userProfile),
          getOrgForFeatures()
        ]);

        let data = orgFeatures || [];
        if (!isEmpty(loggedInOrg)) {
          data.unshift(loggedInOrg);
        }

        return data;
      }
      if (
        rolePermissionsClone.length &&
        editMode &&
        !operationsEditInitialised &&
        !isEmpty(featureData)
      ) {
        if (featureData?.operations?.length) {
          let organizations = cloneDeep(orgList || []);

          if (!organizations.length) {
            organizations = await init();
            dispatch(setOrgList(organizations));
          }

          let operationPayload = featureData?.operations.map((operation: any) => {
            return {
              ...operation,
              apiPermissions: getObjects(operation.apiPermissions, rolePermissionsClone),
              organizations:
                organizations.length > 0 && operation?.organizations?.length > 0
                  ? organizations.filter((org: any) => operation.organizations.includes(org.id))
                  : [],
              status:
                AppConstants.FEATURES_STATUS_VALUES.filter(
                  (item: any) => item.value === operation.status
                ) || []
            };
          });

          setOperations(operationPayload);
          setOperationsClone(operationPayload);
        }
        setOperationsEditInitialised(true);
      }
    }

    updateOperations();
  }, [rolePermissionsClone, featureData]);
  const saveEditResponse = async (data: any) => {
    let editData = cloneDeep(data || featureData);
    if (!featureData && !editData) {
      let response = await getFeature(params.id);
      if (response.status === HTTP_STATUS.HTTP_OK) {
        editData = response.data;
        setFeatureData(editData);
        dispatch(setFeatureReducerData(editData));
      }
    }
    const { tags, description, operations, status, name } = editData;
    let formDataPayload = {
      name,
      description,
      status: AppConstants.FEATURES_STATUS_VALUES.filter((item: any) => item.value === status) || []
    };
    setFormData(formDataPayload);
    setFormDataClone(formDataPayload);
    setBreadCrumb(name);
    setTags(tags);
    setTagsClone(tags);
    setIsLoading(false);
  };

  const isFeatureDataChanged = () => {
    if (!editMode) {
      return false;
    }

    const eq =
      isEqual(operations, operationsClone) &&
      isEqual(formData, formDataClone) &&
      isEqual(tags, tagsClone);

    return eq;
  };

  const setBreadCrumb = (name?: string) => {
    if (editMode && params.id) {
      dispatch(
        setBreadcrump([
          {
            key: RouteConstants.ROUTE_FEATURES_VIEW.replace(':id', params.id),
            label: `${name}`
          }
        ])
      );
    } else {
      dispatch(
        setBreadcrump([
          {
            key: RouteConstants.ROUTE_FEATURES_CREATE,
            label: `${t('T_NEW_FEATURE')}`
          }
        ])
      );
    }
  };

  const savePayload = async () => {
    setIsLoading(true);

    const { name, status, description } = formData;

    const [statusValue] = status;

    const payload: any = {
      name: name.trim(),
      status: statusValue.value,
      description: description.trim()
    };

    let featureResponse;

    const transformedOperations = operations.map((operation: any) => {
      return {
        ...operation,
        status: operation.status[0].value,
        organizations: operation.organizations.map((org: any) => org.id),
        apiPermissions: operation.apiPermissions
          .filter((permission: any) => typeof permission === 'object')
          .map((permission: any) => permission.apiPermission)
      };
    });

    payload.operations = transformedOperations;

    const isValidTagInput = () => {
      if (tagInput.key?.length && tagInput.expiredOn?.length) {
        return true;
      }
      return false;
    };

    if (tags.length || isValidTagInput()) {
      let transformedTags = tags.length
        ? tags.map((tag: any) => {
            return {
              ...tag,
              expiredOn: moment(tag.expiredOn)
                .utc()
                .format(AppConstants.DEFAULT_METADATA_INSTANCE_UTC_DATETIME_FORMAT)
            };
          })
        : [];

      if (isValidTagInput()) {
        transformedTags = [
          ...transformedTags,
          {
            ...tagInput,
            expiredOn: moment(tagInput.expiredOn)
              .utc()
              .format(AppConstants.DEFAULT_METADATA_INSTANCE_UTC_DATETIME_FORMAT)
          }
        ];
      }
      payload.tags = transformedTags;
    }
    featureResponse = await createFeature(payload);

    const { data, status: featureStatus } = featureResponse;
    if (![HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(featureStatus)) {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: getFailureMessage(featureResponse),
          isToastActive: true,
          toastType: 'error'
        })
      );
      return;
    }

    dispatch(
      setToastData({
        toastMessage: t('T_FEATURE_CREATED_SUCCESS_MSG'),
        isToastActive: true,
        toastType: 'success'
      })
    );
    if (!isModalMode) navigate(RouteConstants.ROUTE_FEATURES_LIST);
    else setNewDataLoaded(data);
    setIsLoading(false);
  };

  const transformTags = (tags: Array<any>) => {
    return tags.map((tag: any) => {
      return {
        ...tag,
        expiredOn: moment(tag.expiredOn)
          .utc()
          .format(AppConstants.DEFAULT_METADATA_INSTANCE_UTC_DATETIME_FORMAT)
      };
    });
  };

  const saveEditPayload = async () => {
    setIsLoading(true);
    let sendToastFlag = false;
    let featureResponse;

    const { name, status, description } = formData;
    const [statusValue] = status;

    const payload = {
      name: name.trim(),
      status: statusValue.value,
      description: description.trim(),
      tags: tags?.length ? transformTags(tags) : []
    };

    let operationPayload = operations.map((operation) => {
      return {
        ...operation,
        status: operation.status[0].value,
        organizations: operation.organizations.map((org: any) => org.id),
        apiPermissions: operation.apiPermissions
          .filter((permission: any) => typeof permission === 'object')
          .map((permission: any) => permission.apiPermission)
      };
    });

    let deletedOperations = differenceBy(operationsClone, operations, 'id');
    const newData = differenceBy(operationPayload, operationsClone, 'id');
    const updatedIntersection = intersectionBy(operationsClone, operationPayload, 'id');
    const updated = updatedIntersection
      .map((item) => {
        const match = operationPayload.find((op) => op.id === item.id);
        if (match) {
          const normalizedItem = {
            ...item,
            status: item.status.map((s: any) => s.value).join(','),
            apiPermissions: item.apiPermissions.map((p: any) => p.apiPermission)
          };

          const normalizedMatch = {
            ...match,
            status: match.status,
            apiPermissions: match.apiPermissions
          };

          const changedValues = Object.keys(normalizedMatch).reduce((changes: any, key) => {
            if (!isEqual(normalizedItem[key], normalizedMatch[key])) {
              changes[key] = normalizedMatch[key];
            }
            return changes;
          }, {});

          if (Object.keys(changedValues).length > 0) {
            return { id: item.id, ...changedValues };
          }
        }
        return null;
      })
      .filter(Boolean);

    if (deletedOperations.length) {
      deletedOperations = deletedOperations.map((operation) => operation.actionName);
    }

    try {
      const promises = [];

      if (newData && newData.length > 0) {
        promises.push(addOperationsToFeature(params?.id, { operations: newData }));
      }

      if (updated.length > 0) {
        updated.forEach((update) => {
          const originalOperation = operationsClone.find((op) => op.id === update.id);
          let initialOrgs = originalOperation.organizations.every(
            (org: any) => typeof org === 'object' && org.id
          )
            ? originalOperation.organizations.map((org: any) => org.id)
            : originalOperation.organizations || [];
          const newOrganizations = update.organizations.length
            ? difference(update.organizations, initialOrgs)
            : [];

          const removedOrganizations = initialOrgs.length
            ? difference(initialOrgs, update.organizations || [])
            : [];

          if (newOrganizations.length > 0) {
            promises.push(
              addOperationOrganizations(params?.id, {
                actions: [originalOperation.actionName],
                allActions: false,
                organizations: newOrganizations
              })
            );
          }

          if (removedOrganizations.length > 0) {
            promises.push(
              removeOperationOrganizations(params?.id, {
                actions: [originalOperation.actionName],
                allActions: false,
                organizations: Array.isArray(removedOrganizations) ? removedOrganizations : []
              })
            );
          }

          if (update.apiPermissions) {
            promises.push(
              updateOperationAPIPermissions(update.id, { permissions: update.apiPermissions })
            );
          }

          promises.push(updateFeatureOperations(update.id, { ...update }));
        });
      }

      if (deletedOperations.length > 0) {
        promises.push(deleteFeatureOperations(params?.id, { actions: deletedOperations }));
      }

      if (promises.length) {
        const promisesExecution = await Promise.all(promises);
        promisesExecution.forEach((promise) => {
          const { status } = promise || {};
          if (!HTTP_STATUS.isSuccess(status)) {
            sendToastFlag = false;
            dispatch(
              setToastData({
                toastMessage: getFailureMessage(promise),
                isToastActive: true,
                toastType: 'error'
              })
            );
          }
        });
        sendToastFlag = true;
      }

      if (!isEqual(formData, formDataClone) || !isEqual(tags, tagsClone)) {
        featureResponse = await updateFeature(params?.id, payload);

        if (![HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(featureResponse.status)) {
          setIsLoading(false);
          setFlowType({
            isCreate: false,
            isEdit: true,
            isReadOnly: true
          });
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(featureResponse),
              isToastActive: true,
              toastType: 'error'
            })
          );
          return;
        }
        if (HTTP_STATUS.isSuccess(featureResponse.status)) {
          sendToastFlag = true;
        }
      } else {
        setIsLoading(false);
      }

      if (sendToastFlag) {
        dispatch(
          setToastData({
            toastMessage: t('T_FEATURE_UPDATE_SUCCESS_MSG'),
            isToastActive: true,
            toastType: 'success'
          })
        );
        setIsLoading(false);
        setOperationsClone(operations);
        if (featureResponse?.data) saveEditResponse(featureResponse.data);
        setFlowType({
          isCreate: false,
          isEdit: true,
          isReadOnly: true
        });
      }
    } catch (error) {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: getFailureMessage(error),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const getRoleAuditInfo = async () => {
    if (!featureData) {
      return new Map<string, any>();
    }
    const { createdBy, updatedBy } = featureData || {};
    const userProfiles: Array<any> = await getUserProfiles(uniq(compact([createdBy, updatedBy])));
    return new Map<string, any>(
      userProfiles.map((userProfile: any) => [userProfile.userId, userProfile])
    );
  };

  const fetchAuditData = async () => {
    const { createdBy, createdOn, updatedBy, updatedOn } = featureData || {};
    const userInfoMap: Map<string, any> = await getRoleAuditInfo();

    setAuditData({
      createdBy: getUserName(userInfoMap.get(createdBy)),
      updatedBy: getUserName(userInfoMap.get(updatedBy)),
      updatedOn: moment(updatedOn).format(AppConstants.DEFAULT_DATE_FORMAT),
      createdOn: moment(createdOn).format(AppConstants.DEFAULT_DATE_FORMAT)
    });
  };

  useEffect(() => {
    if (featureData) {
      fetchAuditData();
    }
  }, [featureData]);

  const getOrgForFeatures = async () => {
    try {
      let response = await getChildOrganizations(
        loggedInOrg?.organizationId,
        AppConstants.QUERY_PARAM_LEVEL_DESENDANTS,
        '',
        AppConstants.DEFAULT_PAGE,
        AppConstants.MAXIMUM_PAGE_SIZE,
        [`name,asc`]
      );
      const { status, data: organizationResp } = response;
      if (status == HTTP_STATUS.HTTP_OK) {
        const { data = [], paging } = organizationResp;
        return data;
      }
      return [];
    } catch (e) {
      return [];
    }
  };

  const FeatureDetails = () => {
    return (
      <div className="flex w-full flex-wrap">
        <div className="mb-5 flex w-full">
          <div className="flex w-2/3 flex-col">
            <InputField
              type={'text'}
              id={'name'}
              label={t('T_FEATURE_NAME')}
              placeholder={''}
              fieldKey={'name'}
              asterixFirst={true}
              required={true}
              formData={formData}
              setFormData={setFormData}
              formError={formError}
              disabled={flowType.isReadOnly}
              handleChange={(e: any) => {
                const { value = '' } = e.target;
                setFormData({ ...formData, name: value });
              }}
              onBlurhandler={(e: any) => {
                const { value = '' } = e.target;
                if (value.length > AppConstants.MAX_LENGTH_IMPORT_NAME) {
                  setFormError({
                    ...formError,
                    name: t('T_VALIDATION_ERR_TEXT_LENGTH_CUSTOM_MSG').replace('${LENGTH}', '1024')
                  });
                } else {
                  setFormError({
                    ...formError,
                    name: ''
                  });
                }
              }}
            />
          </div>
          <div className="w-1/3 pl-5">
            <PickList
              fieldKey={'key'}
              fieldValues={['value']}
              label={t('T_STATUS')}
              selectedItems={formData.status}
              onSelectedChange={(items: any) => {
                setFormData({ ...formData, status: items });
              }}
              items={AppConstants.FEATURES_STATUS_VALUES}
              disabled={flowType.isReadOnly}
              readOnly={false}
              type={PICKLIST_TYPES.SINGLE}
              required
            />
          </div>
        </div>

        <div className="mb-5 flex w-full flex-col">
          <TextAreaField
            fieldKey={'description'}
            type={'text'}
            id={'description'}
            label={t('T_DESCRIPTION')}
            placeholder={''}
            required
            formData={formData}
            setFormData={setFormData}
            formError={formError}
            asterixFirst
            rows={3}
            disabled={flowType.isReadOnly}
            onBlur={(e: any) => {
              const { value = '' } = e.target;
              if (value.length > 4096) {
                setFormError({
                  ...formError,
                  description: t('T_VALIDATION_ERR_TEXT_LENGTH_CUSTOM_MSG').replace(
                    '${LENGTH}',
                    '4096'
                  )
                });
              } else {
                setFormError({
                  ...formError,
                  description: ''
                });
              }
            }}
          ></TextAreaField>
        </div>
      </div>
    );
  };
  const tabConfig = [
    {
      label: t('T_FEATURE_DETAILS'),
      component: (
        <div className="p-4">
          <Card
            title={t('T_FEATURE_DETAIL_CARD_TITLE')}
            icon={UserRole}
            alt={t('T_CARD_ALT_MESSAGE')}
            height=""
            width=""
          >
            {FeatureDetails()}
          </Card>
          <Tags
            tags={tags}
            setTags={setTags}
            disabled={flowType.isReadOnly}
            tagInput={tagInput}
            setTagInput={setTagInput}
          ></Tags>
        </div>
      )
    },
    {
      label: t('T_FEATURE_ACTION_TAGS'),
      component: (
        <div className="flex w-full flex-wrap p-4">
          <OperationsTag
            operations={operations}
            setOperations={setOperations}
            rolePermissions={rolePermissions}
            setRolePermissions={setRolePermissions}
            getAPIPermissions={getAPIPermissions}
            disabled={flowType.isReadOnly}
            getOrgForFeatures={getOrgForFeatures}
          ></OperationsTag>
        </div>
      )
    }
  ];

  const renderStepContent = (step: any) => {
    switch (step) {
      case 1:
        return (
          <div className="p-4">
            {FeatureDetails()}

            <Tags
              tags={tags}
              setTags={setTags}
              disabled={flowType.isReadOnly}
              tagInput={tagInput}
              setTagInput={setTagInput}
            ></Tags>
          </div>
        );
      case 2:
        return (
          <div className="flex w-full flex-wrap p-4">
            <OperationsTag
              operations={operations}
              setOperations={setOperations}
              rolePermissions={rolePermissions}
              setRolePermissions={setRolePermissions}
              getAPIPermissions={getAPIPermissions}
              disabled={flowType.isReadOnly}
              getOrgForFeatures={getOrgForFeatures}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const discardChanges = () => {
    setFormData(formDataClone);
    setOperations(operationsClone);
    setTags(featureData.tags);
    setFormError(FEATURES_FORM_SCHEMA);
    // setRolePermissions(rolePermissionsClone);
    setActiveTab(`${t('T_FEATURE_DETAILS')}`);
  };

  const checkForDeleteConfirm = () => {
    setDeleteConfirmation(true);
  };

  const removeFeature = async () => {
    let error = false;
    setIsLoading(true);
    let toastMessage = `${formData?.name} ${t('T_DELETED_SUCCESS_MESSAGE')}`;
    const response = await deleteFeature(params?.id);
    const { status } = response;
    const success = HTTP_STATUS.isSuccess(status);
    if (!success) {
      error = !error;
      toastMessage = getFailureMessage(response);
    }
    dispatch(
      setToastData({
        toastMessage,
        isToastActive: true,
        toastType: error ? 'error' : 'success'
      })
    );
    setIsLoading(false);
    if (success) {
      navigate(RouteConstants.ROUTE_FEATURES_LIST);
    }
    setDeleteConfirmation(false);
  };

  if (isModalMode) {
    return (
      <LoaderContainer isLoading={isLoading}>
        <div className="w-full">
          <div className="mb-5 mt-3">
            <Stepper stepperData={steps} activeStep={activeStep} className={`w-[420px]`} />
          </div>
          {renderStepContent(activeStep)}

          <ActionButtons
            primaryButtonLabel={activeStep === steps.length ? t('T_SAVE') : t('T_NEXT')}
            primaryButtonAction={() => {
              handleNext();
            }}
            primaryButtonDisabled={!isStepValid(activeStep)}
            secondaryButtonLabel={activeStep === steps.length ? t('T_BACK') : t('T_CANCEL')}
            secondaryButtonAction={handleBack}
          />
        </div>
      </LoaderContainer>
    );
  }

  return (
    <LoaderContainer isLoading={isLoading}>
      {checkDeleteConfirm && (
        <Modal
          overflowVisible={true}
          showCloseIcon={true}
          width="30rem"
          showHeader={true}
          headerText={t('T_FEATURE_DELETE_CONFIRMATION_HEADER')}
          toggleVisiblity={(value: boolean) => {
            setDeleteConfirmation(false);
          }}
          isVisible={checkDeleteConfirm}
          body={
            <>
              <div className="mb-5 flex flex-col">
                <div className="">{t('T_FEATURE_DELETE_CONFIRMATION')}</div>
              </div>
              <ModalButton>
                <BiButton
                  className={'mr-2 text-primary'}
                  type="button"
                  onClick={() => {
                    setDeleteConfirmation(false);
                  }}
                >
                  {t('T_CANCEL')}
                </BiButton>
                <BiButton
                  className={'bg-primary text-white'}
                  type="button"
                  onClick={() => {
                    removeFeature();
                  }}
                >
                  {t('T_DELETE_CONFIRM')}
                </BiButton>
              </ModalButton>
            </>
          }
        />
      )}
      <div
        className={`flex  flex-col  overflow-visible px-2 py-3 ${isModalMode ? 'w-full' : 'w-3/4'}`}
      >
        {!isModalMode && (
          <MenuButtonsPortal>
            <>
              {flowType.isCreate ? (
                <BiButton
                  className={'mr-2 text-primary'}
                  type="button"
                  testId={t('T_BACK_TO_FEATURE_LIST')}
                  onClick={() => {
                    navigate(RouteConstants.ROUTE_FEATURES_LIST);
                  }}
                >
                  {t('T_BACK_TO_FEATURE_LIST')}
                </BiButton>
              ) : (
                <BiButton
                  className={'mr-2 text-primary'}
                  type="button"
                  testId={getIsEditOrReadOnly() ? t('T_BACK_TO_FEATURE_LIST') : t('T_CANCEL')}
                  onClick={() => {
                    getIsEditOrReadOnly() ? navigate(-1) : setisReadOnly(true, true);
                  }}
                >
                  {getIsEditOrReadOnly() ? t('T_BACK_TO_FEATURE_LIST') : t('T_CANCEL')}
                </BiButton>
              )}

              {!getIsEditOrReadOnly() ? (
                <BiButton
                  className={'bg-primary text-white'}
                  type="button"
                  testId={t('T_SAVE')}
                  onClick={() => {
                    flowType.isCreate ? savePayload() : saveEditPayload();
                  }}
                  disabled={
                    !formData.name ||
                    !formData.status ||
                    !formData.description ||
                    !operations.length ||
                    isFeatureDataChanged() ||
                    formError.name ||
                    formError.description
                  }
                >
                  {t('T_SAVE')}
                </BiButton>
              ) : (
                <BiButton
                  className={'bg-primary text-white'}
                  type="button"
                  testId={t('T_EDIT')}
                  onClick={() => {
                    setisReadOnly(false, false);
                  }}
                  disabled={
                    !checkFeaturePermission(
                      AppConstants.FEATURE_KEYS.FEATURE_MANAGEMENT_UPDATE,
                      false
                    )
                  }
                >
                  {t('T_EDIT')}
                </BiButton>
              )}
            </>
          </MenuButtonsPortal>
        )}
        <ToastNotification
          message={toastData.toastMessage}
          isActive={toastData.isToastActive}
          setIsActive={() =>
            dispatch(
              setToastData({
                toastMessage: '',
                isToastActive: false,
                toastType: null
              })
            )
          }
          status={toastData.toastType}
        />

        <div style={{ border: '1px solid #D9D9D9', borderRadius: '7px', overflow: 'auto' }}>
          <TabDrawer
            borderBottom={true}
            tabConfig={tabConfig}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>
      </div>

      {!isLoading && editMode && !isModalMode && (
        <div className="ml-4">
          {(auditData.createdOn || auditData?.createdBy) && (
            <div>
              <AuditInfo
                label={'CREATED'}
                date={auditData?.createdOn || ''}
                user={auditData?.createdBy || ''}
                isUserId={false}
              />
            </div>
          )}
          {(auditData.updatedOn || auditData?.updatedBy) && (
            <div>
              <AuditInfo
                label={'LAST_MODIFIED'}
                date={auditData?.updatedOn || ''}
                user={auditData?.updatedBy || ''}
                isUserId={false}
              />
            </div>
          )}

          {!isLoading &&
            checkFeaturePermission(AppConstants.FEATURE_KEYS.FEATURE_MANAGEMENT_DELETE, false) && (
              <div className="mt-6">
                <BiButton
                  className={
                    'w-48 border border-solid border-red-600 text-danger hover:bg-red-600 hover:text-white'
                  }
                  type="button"
                  onClick={checkForDeleteConfirm}
                  data-testid={`feature_delete_button`}
                >
                  {t('T_FEATURE_DELETE_TEXT')}
                </BiButton>
              </div>
            )}
        </div>
      )}
    </LoaderContainer>
  );
};

export default FeatureDetails;
